import React, { useState, useRef } from 'react';
import { Camera, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ImageCaptureGrid = ({ images, setImages, maxImages = 10, disabled = false }) => {
  const fileInputRef = useRef(null);

  const handleCameraCapture = (e) => {
    const files = Array.from(e.target.files);
    const remaining = maxImages - images.length;
    const filesToAdd = files.slice(0, remaining);

    const newImages = filesToAdd.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    setImages([...images, ...newImages]);
    
    // Reset input to allow capturing same file again
    e.target.value = '';
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
  };

  const triggerCamera = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-4" data-testid="image-capture-grid">
      {/* Hidden camera input - triggers native camera app */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        onChange={handleCameraCapture}
        className="hidden"
        disabled={disabled || images.length >= maxImages}
      />

      {/* Capture Button - triggers native camera */}
      <div>
        <Button
          type="button"
          onClick={triggerCamera}
          disabled={disabled || images.length >= maxImages}
          className="h-12 bg-[#2D5F4F] hover:bg-[#234A3D] text-white w-full"
          data-testid="open-camera-button"
        >
          <Camera className="w-5 h-5 mr-2" />
          Capture Image ({images.length}/{maxImages})
        </Button>
        <p className="text-xs text-[#8A8F9E] mt-2 text-center">
          Opens your device camera app to capture photos
        </p>
      </div>

      {/* Image preview grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {images.map((img, index) => (
            <div
              key={index}
              className="relative aspect-square rounded-lg overflow-hidden border-2 border-[#E5DFD0] bg-white"
              data-testid={`image-preview-${index}`}
            >
              <img
                src={img.preview}
                alt={`Capture ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <Button
                type="button"
                onClick={() => removeImage(index)}
                disabled={disabled}
                className="absolute top-2 right-2 w-8 h-8 p-0 bg-[#B8374A] hover:bg-[#9A2E3D] text-white rounded-full shadow-lg"
                data-testid={`delete-image-${index}-button`}
              >
                <X className="w-4 h-4" />
              </Button>
              <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs font-medium">
                {index + 1}
              </div>
            </div>
          ))}
        </div>
      )}

      {images.length === 0 && (
        <div className="border-2 border-dashed border-[#E5DFD0] rounded-lg p-8 text-center">
          <ImageIcon className="w-12 h-12 mx-auto text-[#8A8F9E] mb-3" />
          <p className="text-[#5A5F6E] text-sm">No images captured yet</p>
          <p className="text-[#8A8F9E] text-xs mt-1">Max {maxImages} images allowed</p>
        </div>
      )}
    </div>
  );
};

export default ImageCaptureGrid;
